import {ValueObject} from '@s-ui/domain'

import BuyerIntentionUserValueObject from './BuyerIntentionUserValueObject.js'

/**
 * Represents a list of buyer intentions
 */
export default class BuyerIntentionsListUserValueObject extends ValueObject {
  #buyerIntentions

  constructor({buyerIntentions}) {
    super()
    this.#buyerIntentions = buyerIntentions
  }

  /**
   * Create from data
   * @param {Object} buyerIntentions
   * @returns {BuyerIntentionsListUserValueObject}
   */
  static create({buyerIntentions} = {}) {
    const buyerIntentionsArray = Array.isArray(buyerIntentions) ? buyerIntentions : []

    const intentions = buyerIntentionsArray.map(intention => {
      // If it's already a BuyerIntentionUserValueObject, return it
      if (intention instanceof BuyerIntentionUserValueObject) {
        return intention
      }
      // Otherwise create from data
      return BuyerIntentionUserValueObject.create(intention)
    })

    return new BuyerIntentionsListUserValueObject({
      buyerIntentions: intentions
    })
  }

  /**
   * Get all buyer intentions
   * @returns {Array<BuyerIntentionUserValueObject>}
   */
  getBuyerIntentions() {
    return this.#buyerIntentions
  }

  /**
   * Get a buyer intention by ID
   * @param {string} id
   * @returns {BuyerIntentionUserValueObject|undefined}
   */
  getById(id) {
    return this.#buyerIntentions.find(intention => intention.getId() === id)
  }

  /**
   * Check if the list is empty
   * @returns {boolean}
   */
  isEmpty() {
    return this.#buyerIntentions.length === 0
  }

  /**
   * Get the count of buyer intentions
   * @returns {number}
   */
  getCount() {
    return this.#buyerIntentions.length
  }

  /**
   * Get value for serialization
   * @returns {Object}
   */
  get value() {
    return {
      buyerIntentions: this.#buyerIntentions.map(intention => intention.value)
    }
  }

  /**
   * Convert to primitive values
   * @returns {Object}
   */
  toPrimitive() {
    return {
      buyerIntentions: this.#buyerIntentions.map(intention => intention.toPrimitive())
    }
  }

  /**
   * Convert to JSON (used with static create)
   * @returns {Object}
   */
  toJSON() {
    return {
      buyerIntentions: this.#buyerIntentions.map(intention => intention.toJSON())
    }
  }
}
